/** @jsxImportSource preact */
import * as m from "../../paraglide/messages.js";
import { formVars } from "../form/form-styles";
import type { WidgetTokens } from "../theme";
import type { WidgetEmailPreview } from "./types";

export interface EmailPreviewProps {
  tokens: WidgetTokens;
  preview: WidgetEmailPreview;
}

/**
 * 邮件预览用的样式：一封信直接放在卡片托盘上。信头（From、To、Reply-to、Subject）是一块白色纸面，
 * 标签淡、值加粗；正文另一块白色纸面，分节标题是淡色小标题；末尾一行淡字落在托盘上。
 */
export const EMAIL_PREVIEW_STYLES = `
.gn-email { box-sizing:border-box; display:flex; flex-direction:column; gap:8px; padding:0; color:var(--gn-form-ink); font-size:13px; line-height:1.45; text-align:left; }
.gn-email-head, .gn-email-body { box-sizing:border-box; margin:0; border-radius:var(--gn-form-r-cell); background:var(--gn-form-sheet); }
.gn-email-head { display:grid; grid-template-columns:max-content minmax(0, 1fr); gap:6px 8px; align-items:baseline; padding:10px; }
.gn-email-row { display:contents; }
.gn-email-row dt { font-size:12px; font-weight:var(--gn-form-strong); color:var(--gn-form-quiet); }
.gn-email-row dd { margin:0; font-weight:700; overflow-wrap:break-word; }
.gn-email-body { display:flex; flex-direction:column; gap:12px; padding:12px 10px; }
.gn-email-section { display:flex; flex-direction:column; gap:2px; }
.gn-email-section-title { font-size:12px; font-weight:var(--gn-form-strong); color:var(--gn-form-quiet); }
.gn-email-line { overflow-wrap:anywhere; }
.gn-email-foot { padding:0 4px 2px; font-size:12px; color:var(--gn-form-quiet); }
`;

/**
 * 发给商家的那封邮件长什么样：内凹井里的一封信，信头（From、To、Reply-to、Subject）、分节正文、末尾一行淡字。
 * 内容由后端生成，原样排版，不改写、不翻译；预览卡（Review your request）用它。
 */
export function EmailPreview({ tokens: t, preview }: EmailPreviewProps) {
  const rows = [
    { label: m.widget_contact_email_from(), value: preview.from },
    { label: m.widget_contact_email_to(), value: preview.to },
    { label: m.widget_contact_email_reply_to(), value: preview.replyTo },
    { label: m.widget_contact_email_subject(), value: preview.subject },
  ];
  return (
    <div
      aria-label={m.widget_contact_email_preview_label()}
      class="gn-email"
      role="group"
      style={formVars(t)}
    >
      <style>{EMAIL_PREVIEW_STYLES}</style>
      <dl class="gn-email-head">
        {rows.map((row) => (
          <div class="gn-email-row" key={row.label}>
            <dt>{row.label}</dt>
            <dd>{row.value}</dd>
          </div>
        ))}
      </dl>
      {preview.sections.length > 0 && (
        <div class="gn-email-body">
          {preview.sections.map((section, index) => (
            <div
              class="gn-email-section"
              key={`${String(index)}:${section.title}`}
            >
              <div class="gn-email-section-title">{section.title}</div>
              <div>
                {section.lines.map((line, lineIndex) => (
                  <div
                    class="gn-email-line"
                    key={`${String(lineIndex)}:${line}`}
                  >
                    {line}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      {preview.footnote !== undefined && (
        <div class="gn-email-foot">{preview.footnote}</div>
      )}
    </div>
  );
}
